import { addMessage } from '../../actions/messagesActions';
import { showSetPasswordModal, validateUser } from '../../actions/userActions';
import Loading from '../common/Loading';
import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Navigate, useParams } from 'react-router';

/**
 * Accepts a token as part of the URL, and sends this to the server as confirmation of a user's account.
 *
 * Can also have a form token added, which will redirect the user to a specific form after logging in
 */
const ValidateUser = ({ validateUser, showSetPasswordModal, dashboard, user }) => {
  const params = useParams();
  const validateToken = params?.validateToken;
  const [hasValidated, setHasValidated] = useState(false);
  const validateRequestStartedRef = useRef(false);
  const formToken = params?.formToken || null;

  const validationComplete = !validateToken || hasValidated;

  // Send the validate API call...
  useEffect(() => {
    if (!validateToken || validateRequestStartedRef.current) {
      return;
    }

    validateRequestStartedRef.current = true;

    // Validate the user from the given token.
    // Pass in a callback that tells the application to re-render once it's validated.
    validateUser(validateToken, () => {
      setHasValidated(true);
    });
  }, [validateToken, validateUser]);

  // After successful validation, check if password reset is pending and show modal
  useEffect(() => {
    if (validateToken && hasValidated && user.sessionId && user.details?.passwordResetPending) {
      // Explicitly show the modal for password reset flow from validation
      showSetPasswordModal(false); // false = no close button
    }
  }, [validateToken, hasValidated, user.sessionId, user.details?.passwordResetPending, showSetPasswordModal]);

  // Wait for validation to complete (missing token skips loading — nothing to validate)
  if (!validationComplete) {
    return <Loading text="Validating user" />;
  }

  // Validation has completed - check if it succeeded or failed
  // If validation completed but we don't have user data, validation failed
  if (validateToken && !user.sessionId) {
    // Validation failed or user not authenticated, redirect to login
    // (The alert will have already been shown in the action)
    return <Navigate to="/login" replace />;
  }

  if (formToken && dashboard?.tiles) {
    // We specified a form - find the form we indicated and redirect to it.
    for (let i = 0, ii = dashboard.tiles.length; i < ii; i++) {
      for (let j = 0, jj = dashboard.tiles[i].formTypes.length; j < jj; j++) {
        for (let k = 0, kk = dashboard.tiles[i].formTypes[j].forms.length; k < kk; k++) {
          if (dashboard.tiles[i].formTypes[j].forms[k].formId === formToken) {
            return <Navigate to={`/form/${dashboard.tiles[i].formTypes[j].id}/${formToken}`} replace />;
          }
        }
      }
    }
  }

  // If we didn't specify a valid form, redirect to the home page
  return <Navigate to="/" replace />;
};

const mapStateToProps = (state) => ({
  dashboard: state.dashboard,
  user: state.user,
});

const mapDispatchToProps = {
  addMessage,
  validateUser,
  showSetPasswordModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ValidateUser);
